import type { Item } from '@/stores/canvasStore';
import { GRID_SNAP, MIN_ITEM_X, MIN_ITEM_Y } from '@/lib/canvasConstants';

/**
 * Helpers for placing a Discover item on a board the user is not looking at.
 * The canvas viewport (pan/zoom) belongs to the open board, so placement is
 * computed from the target board's own items instead.
 */

const PLACEMENT_GAP = 40;

/**
 * How adding `item` to `targetBoardId` should behave:
 * - 'move': the item has no board yet, so it simply moves onto the target
 * - 'copy': the item already lives on the target, so add a plain duplicate
 *   (same as adding a search result that is on the current board)
 * - 'choose': it lives on another board, so ask duplicate vs sync
 */
export type AddToCanvasMode = 'move' | 'copy' | 'choose';

export function getAddToCanvasMode(
  item: Pick<Item, 'board_id'>,
  targetBoardId: string
): AddToCanvasMode {
  if (!item.board_id) return 'move';
  if (item.board_id === targetBoardId) return 'copy';
  return 'choose';
}

/** Board links and synced items reference other rows; copying them breaks those links. */
export function canAddToCanvas(item: Pick<Item, 'item_type'>): boolean {
  return item.item_type !== 'board_link' && item.item_type !== 'synced_item';
}

const snap = (n: number) => Math.round(n / GRID_SNAP) * GRID_SNAP;

/**
 * Top-left position for a new item on `boardId`: to the right of everything
 * already there, aligned with the topmost item. Empty boards get the first
 * safe spot below the navbar and right of the sidebar.
 */
export function getPlacementOnBoard(
  allItems: Pick<Item, 'id' | 'board_id' | 'position_x' | 'position_y' | 'width'>[],
  boardId: string,
  excludeItemId?: string
): { x: number; y: number } {
  const boardItems = allItems.filter(i => i.board_id === boardId && i.id !== excludeItemId);
  if (boardItems.length === 0) {
    return { x: snap(MIN_ITEM_X + PLACEMENT_GAP), y: snap(MIN_ITEM_Y + PLACEMENT_GAP) };
  }

  const maxX = Math.max(...boardItems.map(i => i.position_x + i.width));
  const minY = Math.min(...boardItems.map(i => i.position_y));
  return {
    x: snap(maxX + PLACEMENT_GAP),
    y: snap(Math.max(minY, MIN_ITEM_Y)),
  };
}
